import { contrastRatio } from './colorSystemPresentation.mjs';
import { logoLines } from './logoCompositionV1.mjs';

/** @typedef {{font_weight:number,font_style:'normal'|'italic',text_transform:string,letter_spacing_em:number}} Treatment */
/** @typedef {{templateId:string,templateVersion:number,rendererVersion:number,width:number,height:number,colorSpace:'srgb',background:string,color:string,nodes:Array<object>}} ThankYouScene */

// 7:9 digital master. Physical proportion only; no print DPI or bleed claims.
export const THANK_YOU_TEMPLATE = Object.freeze({
  id: 'digital_thank_you_v1', version: 3, rendererVersion: 3,
  width: 1400, height: 1800, physicalRatio: [70,90], colorSpace: 'srgb', safeMargin: 140,
  maxText: 500, maxExtra: 90, maxHeading: 60, bodySize: 66, bodyLeading: 86,
  wrap: 'measured-words-explicit-newlines', fontRoles: ['signature','body','headline'],
});
export const CARD_LAYOUTS = Object.freeze([
  {id:'editorial-light',name:'Editorial Light'},
  {id:'editorial-dark',name:'Editorial Dark'},
  {id:'signature-minimal',name:'Signature Minimal'},
]);
export const DEFAULT_CARD_DESIGN = Object.freeze({layout:'editorial-light',signature:true,signatureName:'',signatureSize:'small',signaturePosition:'bottom',symbol:false,symbolSize:'small',symbolPosition:'top',align:'center'});
export const EXAMPLE_HEADING = 'Спасибо';
export const EXAMPLE_THANK_YOU = 'За ваш заказ и доверие.\nПусть этот момент будет особенным.';
export class CardLayoutError extends Error {}

// Owner-approved application labels, never a replacement for Brand Profile.
export function cardSignatureChoices(brandName) {
  return ['oksana anokina','anokina studio'].includes(brandName.trim().toLowerCase())
    ? ['Oksana Anokina','Anokina Studio'] : [brandName];
}
export function cardColorPairs(board) {
  if (!board?.ready) return [];
  const dark=board.colors.find(c=>c.role==='primary_dark'), light=board.colors.find(c=>c.role==='primary_light');
  if (!dark || !light || contrastRatio(dark.hex,light.hex)<4.5) return [];
  const pairs=board.colors.map(bg=>{
    const candidates=board.colors.filter(fg=>contrastRatio(bg.hex,fg.hex)>=4.5);
    candidates.sort((a,b)=>contrastRatio(bg.hex,b.hex)-contrastRatio(bg.hex,a.hex));
    const fg=candidates[0];
    // A supporting field may need a readable approved paper/text pair inside it.
    return {background:bg.hex,text:fg?.hex || dark.hex,paper:fg?bg.hex:light.hex,
      name:bg.name,ratio:contrastRatio(fg?bg.hex:light.hex,fg?.hex || dark.hex)};
  });
  const inverse={background:dark.hex,text:light.hex,paper:dark.hex,name:dark.name,ratio:contrastRatio(dark.hex,light.hex)};
  const neutral=board.colors.find(c=>c.role==='neutral' && contrastRatio(dark.hex,c.hex)>=4.5);
  if(neutral)pairs.push({...inverse,text:neutral.hex,name:`${dark.name} · ${neutral.name}`,ratio:contrastRatio(dark.hex,neutral.hex)});
  // Start with the light editorial pairing; preserve the remaining palette order.
  return pairs.sort((a,b)=>(b.background===light.hex)-(a.background===light.hex)).slice(0,6);
}

const transform = (text, treatment) => treatment.text_transform === 'uppercase' ? text.toUpperCase() : text;

function style(ctx, family, treatment, size) {
  if (!family || ![400,500,600,700,300,200,100,800,900].includes(treatment.font_weight) || !['normal','italic'].includes(treatment.font_style))
    throw new CardLayoutError('Не удалось проверить начертание шрифта.');
  ctx.font = `${treatment.font_style} ${treatment.font_weight} ${size}px "${family}"`;
  ctx.textBaseline = 'alphabetic';
  ctx.fontKerning = 'normal';
  const spacing = treatment.letter_spacing_em || 0;
  if (spacing && !('letterSpacing' in ctx)) throw new CardLayoutError('Этот браузер не поддерживает точные интервалы. Открой карточку в актуальном браузере.');
  if ('letterSpacing' in ctx) ctx.letterSpacing = `${spacing * size}px`;
}

export function wrapMeasured(ctx, text, width, maxLines) {
  const lines = [];
  for (const paragraph of text.replace(/\r\n?/g,'\n').replace(/\t/g,' ').split('\n')) {
    let line = '';
    for (const word of paragraph.trim().split(/\s+/).filter(Boolean)) {
      if (ctx.measureText(word).width > width) throw new CardLayoutError('Одно слово слишком длинное. Сократи его или добавь пробел.');
      const next = line ? `${line} ${word}` : word;
      if (ctx.measureText(next).width > width && line) { lines.push(line); line = word; } else line = next;
    }
    lines.push(line);
  }
  if (lines.length > maxLines) throw new CardLayoutError('Текст не помещается. Сократи его — размер букв останется читаемым.');
  return lines;
}

function textNodes(ctx, {text, family, treatment, size, leading, zone, centered = false, maxLines}) {
  style(ctx, family, treatment, size);
  const lines = wrapMeasured(ctx, transform(text, treatment), zone[2], maxLines);
  const height = lines.length * leading;
  if (height > zone[3]) throw new CardLayoutError('Текст не помещается. Сократи его.');
  const top = zone[1] + (zone[3] - height) / 2;
  return lines.map((line, i) => {
    const measured = ctx.measureText(line);
    const x = centered ? zone[0] + (zone[2] - measured.width) / 2 : zone[0];
    const y = top + i * leading + size;
    const left = x - (measured.actualBoundingBoxLeft || 0), right = x + (measured.actualBoundingBoxRight ?? measured.width);
    const ascent = measured.actualBoundingBoxAscent ?? size, descent = measured.actualBoundingBoxDescent ?? size * .25;
    if (left < zone[0] - 1 || right > zone[0] + zone[2] + 1 || y - ascent < zone[1] || y + descent > zone[1] + zone[3])
      throw new CardLayoutError('Буквы выходят за безопасные поля. Сократи текст.');
    return {kind:'text',text:line,x,y,size,family,treatment};
  });
}

/** @returns {ThankYouScene} */
export function resolveThankYouScene(ctx, {board,text,extra,heading=EXAMPLE_HEADING,pair,families,logoImage,symbolImage,design={}}) {
  if(!board.ready || !cardColorPairs(board).some(p=>p.background===pair.background && p.text===pair.text && p.paper===pair.paper))
    throw new CardLayoutError('Сначала проверь актуальные цвета и шрифты.');
  const d={...DEFAULT_CARD_DESIGN,...design};
  if(!CARD_LAYOUTS.some(p=>p.id===d.layout) || !['left','center'].includes(d.align)
    || !['small','medium'].includes(d.signatureSize) || !['small','medium'].includes(d.symbolSize)
    || !['top','bottom'].includes(d.signaturePosition) || !['top','bottom','signature-left'].includes(d.symbolPosition))
    throw new CardLayoutError('Выбери предусмотренное оформление.');
  const signature=d.signatureName || board.brandName;
  if(d.signature && ![board.brandName,...cardSignatureChoices(board.brandName)].includes(signature))
    throw new CardLayoutError('Фирменная подпись не подтверждена.');
  if(!text.trim() || text.length>500 || extra.length>90 || !heading.trim() || heading.length>60)
    throw new CardLayoutError('Добавь короткий текст благодарности.');
  const t=THANK_YOU_TEMPLATE,nodes=[],minimal=d.layout==='signature-minimal',centered=d.align==='center';
  const inset=pair.paper!==pair.background;
  if(inset)nodes.push({kind:'panel',x:90,y:90,width:1220,height:1620,color:pair.paper});
  const zoneX=minimal?210:160, zoneWidth=minimal?980:1080;
  const add=(value,role,size,leading,zone,maxLines)=>nodes.push(...textNodes(ctx,{text:value,family:families[role],treatment:board.roles[role].treatment,size,leading,zone,centered,maxLines}));
  // Three fixed optical hierarchies, with separate identity and message zones.
  const top=minimal?530:d.layout==='editorial-dark'?500:480;
  add(heading,'headline',minimal?112:132,minimal?140:158,[zoneX,top,zoneWidth,340],2);
  add(text,'body',t.bodySize,t.bodyLeading,[zoneX,top+360,zoneWidth,430],5);
  if(extra.trim())add(extra,'body',46,60,[zoneX,1330,zoneWidth,120],2);
  const identityY=d.signaturePosition==='top'?(board.logo?240:180):1490;
  const horizontal=d.signature && d.symbol && d.symbolPosition==='signature-left';
  const signatureSize=d.signatureSize==='medium'?58:46;
  if(d.signature)add(signature,'signature',signatureSize,72,[140,identityY,1120,110],1);
  if(d.symbol){
    if(!symbolImage || !['oksana anokina','anokina studio'].includes(board.brandName.trim().toLowerCase()))
      throw new CardLayoutError('Фирменный знак недоступен.');
    if(horizontal){
      const label=nodes[nodes.length-1];
      style(ctx,label.family,label.treatment,label.size);ctx.textAlign='left';
      const m=ctx.measureText(label.text);
      const left=m.actualBoundingBoxLeft, right=m.actualBoundingBoxRight;
      const ascent=m.actualBoundingBoxAscent, descent=m.actualBoundingBoxDescent;
      if(![left,right,ascent,descent].every(Number.isFinite) || ascent+descent<=0
        || !symbolImage.width || !symbolImage.height)
        throw new CardLayoutError('Не удалось проверить границы фирменной подписи.');
      const height=(ascent+descent)*(d.symbolSize==='medium'?1.6:1.25);
      const width=height*symbolImage.width/symbolImage.height;
      const gap=Math.max(18,signatureSize*.45), blockWidth=width+gap+left+right;
      const x=centered?(t.width-blockWidth)/2:zoneX;
      const y=label.y+(descent-ascent)/2-height/2;
      label.x=x+width+gap+left;
      if(x<t.safeMargin || x+blockWidth>t.width-t.safeMargin || y<identityY || y+height>identityY+110)
        throw new CardLayoutError('Фирменный блок не помещается. Выбери маленькую подпись.');
      nodes.push({kind:'symbol',image:symbolImage,x,y,width,height});
    }else{
      const size=d.symbolSize==='medium'?112:82;
      // With the signature hidden, the key remains a standalone top element.
      const position=d.symbolPosition==='signature-left'?'top':d.symbolPosition;
      const y=position==='top'?(board.logo?355:330):1580;
      nodes.push({kind:'symbol',image:symbolImage,x:centered?(1400-size)/2:zoneX,y,width:size,height:size});
    }
  }
  // Existing approved logo stays exact and separate from editable signature/key.
  const logo=board.logo;
  if(logo?.mode==='existing'){
    if(!logoImage?.width || !logoImage?.height)throw new CardLayoutError('Не удалось открыть утверждённый логотип.');
    const scale=Math.min(300/logoImage.width,100/logoImage.height);
    nodes.push({kind:'image',image:logoImage,x:centered?(1400-logoImage.width*scale)/2:160,y:100,width:logoImage.width*scale,height:logoImage.height*scale});
  }else if(logo?.mode==='wordmark'){
    const c=logo.composition,lines=logoLines(board.brandName,c),size=30,leading=38;
    style(ctx,families.logo,c,size);
    if(lines.some(line=>ctx.measureText(line).width>1050))throw new CardLayoutError('Логотип не помещается. Проверь его композицию.');
    nodes.push({kind:'panel',x:140,y:80,width:1120,height:140,color:c.light_background_hex});
    lines.forEach((line,i)=>nodes.push({kind:'text',text:line,x:c.alignment==='center'?700:165,y:125+i*leading,size,family:families.logo,treatment:c,align:c.alignment==='center'?'center':'left',color:c.color_hex}));
    if(c.variant==='descriptor' && c.descriptor)nodes.push({kind:'text',text:c.descriptor,x:c.alignment==='center'?700:165,y:125+lines.length*leading,size:20,family:families.logo,treatment:{...c,letter_spacing_em:0},align:c.alignment==='center'?'center':'left',color:c.color_hex});
  }
  for(const node of nodes.filter(n=>n.kind==='text')){
    style(ctx,node.family,node.treatment,node.size);ctx.textAlign=node.align || 'left';const m=ctx.measureText(node.text);
    if(node.x-m.actualBoundingBoxLeft<90 || node.x+m.actualBoundingBoxRight>1310 || node.y-m.actualBoundingBoxAscent<90 || node.y+m.actualBoundingBoxDescent>1710)
      throw new CardLayoutError('Буквы выходят за безопасные поля. Сократи текст.');
  }
  return {templateId:t.id,templateVersion:t.version,rendererVersion:t.rendererVersion,width:t.width,height:t.height,colorSpace:t.colorSpace,background:pair.background,color:pair.text,nodes};
}

export function paintThankYouScene(ctx, scene) {
  ctx.fillStyle = scene.background; ctx.fillRect(0,0,scene.width,scene.height);
  for (const node of scene.nodes) {
    if (node.kind === 'symbol') {
      const mask=document.createElement('canvas');mask.width=node.width;mask.height=node.height;
      const m=mask.getContext('2d');m.drawImage(node.image,0,0,node.width,node.height);
      m.globalCompositeOperation='source-in';m.fillStyle=scene.color;m.fillRect(0,0,node.width,node.height);ctx.drawImage(mask,node.x,node.y);
    }
    else if (node.kind === 'image') ctx.drawImage(node.image,node.x,node.y,node.width,node.height);
    else if (node.kind === 'panel') { ctx.fillStyle=node.color; ctx.fillRect(node.x,node.y,node.width,node.height); }
    else {
      style(ctx,node.family,node.treatment,node.size);
      ctx.textAlign=node.align || 'left'; ctx.fillStyle=node.color || scene.color;
      ctx.fillText(node.text,node.x,node.y);
    }
  }
}
